import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";


import {
  deleteProperty,
  getAllProperties,
  clearMessage,
} from "@/services/productServices/productActions";

import NotificationAlertOptions from "@/layouts/Alerts";
import ReactBSAlert from "react-bootstrap-sweetalert";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";

import DisplayPropertiesView from "./DisplayPropertiesView";

// A property row of the products slice, which is untyped JS (productSlice.js).
type PropertyRowHandler = (row: any) => void;

type DisplayPropertiesContainerProps = {
  handleUpdate?: PropertyRowHandler;
  handleLabel?: PropertyRowHandler;
  handleOrder?: PropertyRowHandler;
};

export default function DisplayPropertiesContainer({
  handleUpdate,
  handleLabel,
  handleOrder,
}: DisplayPropertiesContainerProps) {
  const dispatch = useAppDispatch();

  const products    = useAppSelector((s) => s.products);
  const permission  = useCurrentPermission();
  const currentSite = useAppSelector((s) => s.sites?.site || {});
  const [properties, setProperties] = useState<any[]>([]); // any: copies products.properties, typed any there (productSlice.ts:51)
  const [alert, setAlert] = useState<React.ReactNode>(null);

  
  useEffect(() => {
    dispatch(getAllProperties());
  }, [dispatch]);

  // Success message ==> clear
  useEffect(() => {
    if (products?.message) {
      dispatch(clearMessage());
    }
  }, [products?.message, dispatch]);

  // Sync properties from store
  useEffect(() => {
    if (Array.isArray(products?.properties)) {
      setProperties(products.properties);
    }
  }, [products?.properties]);

  
  const reformattedProp = useMemo(() => {
    const siteId = currentSite?._id;
    return (properties || []).map((prop) => {
      if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length > 0) {
        return { ...prop, label: prop.label.filter((l) => l.siteid === siteId) };
      }
      if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length === 0) {
        return { ...prop, label: [{ value: { label: "" } }] };
      }
      return { ...prop, label: [{ value: { label: prop.label } }] };
    });
  }, [properties, currentSite?._id]);

 
  const hideAlert = useCallback(() => setAlert(null), []);

  const questionAlert = useCallback(
    (id) => {
      setAlert(
        // @ts-expect-error react-bootstrap-sweetalert prop-types
        <ReactBSAlert
          custom
          style={{ display: "block", marginTop: "-100px" }}
          title="Would you like to remove this Property?"
          customIcon={
            <div
              className="swal2-icon swal2-question swal2-animate-question-icon"
              style={{ display: "flex" }}
            >
              <span className="swal2-icon-text">?</span>
            </div>
          }
          onConfirm={async () => {
            hideAlert();
            try {
              // SCRUM-1021 follow-up: deleteProperty is a createAppAsyncThunk now,
              // so await .unwrap() and toast success ONLY after it resolves — this
              // used to fire unconditionally, a false success on a failed delete.
              await dispatch(deleteProperty(id)).unwrap();
              NotificationAlertOptions("success", "Property Deleted with success");
            } catch (err) {
              // SCRUM-2363: a refusal with referrers opens the list (SCRUM-2352);
              // any other failure is surfaced centrally via appErrorMiddleware.
              if (isDeleteRefusal(err)) {
                setAlert(
                  <ReferrersDialog
                    message={refusalMessage(err)}
                    referrers={err.referrers}
                    onClose={hideAlert}
                  />
                );
              }
            }
          }}
          onCancel={hideAlert}
          confirmBtnBsStyle="default"
          confirmBtnText="Ok"
        >
          Delete Operation
        </ReactBSAlert>
      );
    },
    [dispatch, hideAlert]
  );

 
  const columns = useMemo(() => {
    const base = ([
      { accessorKey: "name", header: "Name" },
      {
        accessorFn: (row) => row.label?.[0]?.value?.label,
        header: "Label",
      },
      { accessorKey: "description", header: "Description" },
      { accessorKey: "type", header: "Type" },
      { accessorKey: "order_listing", header: "Order Listing" },
      { accessorKey: "order_product", header: "Order Product" },
    ] as any[]);

    if (permission?.edit || permission?.publish) {
      base.push({
        header: "ACTION",
        cell: ({ row }) => (
          <div>
            <button
              className="btn btn-round btn-icon btn-sm btn-primary mr-1"
              onClick={() => handleUpdate?.(row.original)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="ni ni-settings-gear-65" />
              </span>
              <span className="btn-inner--text">Update</span>
            </button>

            <button
              className="btn btn-round btn-icon btn-sm btn-danger mr-1"
              onClick={() => questionAlert(row.original._id)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="fas fa-trash" />
              </span>
              <span className="btn-inner--text">Delete</span>
            </button>

            <button
              className="btn btn-round btn-icon btn-sm btn-success mr-1"
              onClick={() => handleLabel?.(row.original)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="ni ni-fat-add" />
              </span>
              <span className="btn-inner--text">Label</span>
            </button>

            <button
              className="btn btn-round btn-icon btn-sm btn-info"
              onClick={() => handleOrder?.(row.original)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="ni ni-ruler-pencil" />
              </span>
              <span className="btn-inner--text">Order</span>
            </button>
          </div>
        ),
      });
    }

    return base;
  }, [permission?.edit, permission?.publish, handleUpdate, handleLabel, handleOrder, questionAlert]);

  return (
    <DisplayPropertiesView
      alert={alert}
      columns={columns}
      reformattedProp={reformattedProp}
    />
  );
}
